import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import {
  getCompanySettings,
  saveCompanySettings,
  CompanySettings,
  DEFAULT_COMPANY_SETTINGS,
} from '@/services/firestore'
import { friendlyError } from '@/utils/friendlyError'
import { QRCodeSVG } from 'qrcode.react'
import {
  Building2,
  Save,
  RotateCcw,
  CheckCircle2,
  Phone,
  Mail,
  Globe,
  MapPin,
  FileText,
  ShieldCheck,
  Eye,
  Sparkles,
  Info,
} from 'lucide-react'

export const Route = createFileRoute('/admin/company')({
  component: CompanySettingsPage,
})

function CompanySettingsPage() {
  const { user } = useAuth()
  const [form, setForm] = useState<CompanySettings>(DEFAULT_COMPANY_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedSuccess, setSavedSuccess] = useState(false)
  const [showFbrPreview, setShowFbrPreview] = useState(true)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    setLoading(true)
    try {
      const data = await getCompanySettings()
      setForm(data)
    } catch (err: any) {
      console.error(err)
      alert(friendlyError(err, 'Failed to load company profile'))
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!form.companyName.trim()) {
      alert('Company Name is required.')
      return
    }
    if (!form.ntn.trim()) {
      alert('NTN (National Tax Number) is required.')
      return
    }

    setSaving(true)
    setSavedSuccess(false)
    try {
      await saveCompanySettings(form, user?.email || 'admin')
      setSavedSuccess(true)
      setTimeout(() => setSavedSuccess(false), 4000)
    } catch (err: any) {
      console.error(err)
      alert(friendlyError(err, 'Failed to save company settings'))
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    if (
      confirm(
        'Are you sure you want to reset company details to default settings?'
      )
    ) {
      setForm({ ...DEFAULT_COMPANY_SETTINGS })
    }
  }

  if (loading) {
    return (
      <div className="p-4 pt-14 lg:pt-5 lg:p-6 bg-[#f0f5ff] min-h-screen animate-pulse">
        <div className="h-8 w-60 bg-slate-200 rounded-lg mb-6" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-[600px] bg-white rounded-3xl border border-slate-200/60" />
          <div className="h-[600px] bg-white rounded-3xl border border-slate-200/60" />
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 pt-14 lg:pt-5 lg:p-6 bg-[#f0f5ff] min-h-screen font-sans">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-black text-slate-800 flex items-center gap-2.5">
            <Building2 className="w-6 h-6 text-blue-600" />
            Company &amp; Invoice Settings
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Customize company name, address, NTN, STN, contact info, and printable invoice layout
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-1.5 px-4 py-2 bg-white hover:bg-slate-50 text-slate-600 rounded-xl border border-slate-200/80 text-xs font-bold shadow-sm transition-all"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset Defaults
          </button>

          <button
            type="button"
            onClick={() => handleSave()}
            disabled={saving}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-2 rounded-xl text-xs font-bold shadow-lg shadow-blue-500/25 transition-all transform active:scale-95 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving Changes...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Success Notification Banner */}
      {savedSuccess && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-center gap-3 text-emerald-800 shadow-sm animate-in fade-in duration-300">
          <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
          <div>
            <h4 className="text-xs font-bold">Company profile updated successfully!</h4>
            <p className="text-[11px] text-emerald-700 mt-0.5">
              All newly generated FBR invoices, POS sales receipts, quotations, and purchase documents will now reflect these updated details.
            </p>
          </div>
        </div>
      )}

      {/* Main Grid: Form Left (2 cols) & Live Preview Right (1 col) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Form Area (7 Cols) */}
        <form onSubmit={handleSave} className="lg:col-span-7 space-y-6">
          {/* Section 1: Business Identity */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200/80 shadow-sm space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
              <Building2 className="w-4 h-4 text-blue-600" />
              <h3 className="font-black text-sm text-slate-800">Business Identity &amp; Header</h3>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Company / Business Name * (Printed as Main Invoice Header)
                </label>
                <input
                  required
                  value={form.companyName}
                  onChange={(e) => setForm({ ...form, companyName: e.target.value })}
                  placeholder="e.g. DYNAMIC INDUSTRIAL SOLUTIONS"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 font-bold text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50/50"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    Tagline / Sub-Title
                  </label>
                  <input
                    value={form.tagline || ''}
                    onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                    placeholder="e.g. Engineering & Industrial Supplies"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    POS Terminal / Machine ID
                  </label>
                  <input
                    value={form.posId || ''}
                    onChange={(e) => setForm({ ...form, posId: e.target.value })}
                    placeholder="e.g. 1 or POS-001"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-slate-700 font-mono focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Tax & Legal Identifiers */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200/80 shadow-sm space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <h3 className="font-black text-sm text-slate-800">Tax &amp; Legal Identifiers (FBR Registered)</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  National Tax Number (NTN) *
                </label>
                <input
                  required
                  value={form.ntn}
                  onChange={(e) => setForm({ ...form, ntn: e.target.value })}
                  placeholder="e.g. 4113099 or 4113099-6"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 font-mono font-bold text-slate-800 focus:ring-2 focus:ring-blue-500 bg-slate-50/50"
                />
                <span className="text-[10px] text-slate-400 mt-1 block">Used for FBR API authentication &amp; invoices</span>
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Sales Tax Number (STN / STRN) *
                </label>
                <input
                  required
                  value={form.stn}
                  onChange={(e) => setForm({ ...form, stn: e.target.value })}
                  placeholder="e.g. 0300411300919"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 font-mono font-bold text-slate-800 focus:ring-2 focus:ring-blue-500 bg-slate-50/50"
                />
                <span className="text-[10px] text-slate-400 mt-1 block">Printed on tax invoice header</span>
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Province / State
                </label>
                <input
                  value={form.province || ''}
                  onChange={(e) => setForm({ ...form, province: e.target.value })}
                  placeholder="e.g. Punjab"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Country
                </label>
                <input
                  value={form.country || ''}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  placeholder="e.g. Pakistan"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Physical Address & Contact Info */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200/80 shadow-sm space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
              <MapPin className="w-4 h-4 text-purple-600" />
              <h3 className="font-black text-sm text-slate-800">Physical Address &amp; Contact Details</h3>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Complete Street Address * (Printed on Line 2 &amp; 3)
                </label>
                <textarea
                  rows={2}
                  required
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="e.g. SUIT NO. 411 BLOCK-F1, MAIN BOULEVARD PAK ARAB SOCIETY, 18-KM FEROZEPUR ROAD"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-slate-800 focus:ring-2 focus:ring-blue-500 font-medium leading-relaxed"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                    City *
                  </label>
                  <input
                    required
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="e.g. Lahore"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-slate-800 font-medium focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1 flex items-center gap-1">
                    <Phone className="w-3 h-3 text-slate-400" />
                    Phone / Tel
                  </label>
                  <input
                    value={form.phone || ''}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="e.g. +92 300 4113099"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1 flex items-center gap-1">
                    <Mail className="w-3 h-3 text-slate-400" />
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email || ''}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="e.g. info@dis.com.pk"
                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1 flex items-center gap-1">
                  <Globe className="w-3 h-3 text-slate-400" />
                  Website URL
                </label>
                <input
                  value={form.website || ''}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="e.g. https://dynamicindustrialsolutions.online"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Section 4: Document Terms & Footers */}
          <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200/80 shadow-sm space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
              <FileText className="w-4 h-4 text-amber-600" />
              <h3 className="font-black text-sm text-slate-800">Invoice Customization &amp; Terms</h3>
            </div>

            <div className="space-y-3 text-xs">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Default Invoice Title
                </label>
                <input
                  value={form.invoiceTitle || ''}
                  onChange={(e) => setForm({ ...form, invoiceTitle: e.target.value })}
                  placeholder="e.g. Sales Tax Invoice"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Invoice Footer Remarks / Greeting
                </label>
                <input
                  value={form.invoiceFooterNotes || ''}
                  onChange={(e) => setForm({ ...form, invoiceFooterNotes: e.target.value })}
                  placeholder="e.g. Thank you for your business!"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-slate-700 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                  Default Terms &amp; Conditions
                </label>
                <textarea
                  rows={3}
                  value={form.termsAndConditions || ''}
                  onChange={(e) => setForm({ ...form, termsAndConditions: e.target.value })}
                  placeholder="Enter business terms..."
                  className="w-full p-3 rounded-xl border border-slate-200 text-xs focus:ring-2 focus:ring-blue-500 font-mono leading-relaxed"
                />
              </div>
            </div>
          </div>
        </form>

        {/* Right Area: Real-Time Live Invoice Header Preview (5 Cols) */}
        <div className="lg:col-span-5 space-y-4">
          <div className="sticky top-6">
            <div className="bg-white rounded-3xl border border-slate-200/80 shadow-md p-5 space-y-4">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-blue-600" />
                  <h3 className="font-black text-xs text-slate-800">Live Print Header Preview</h3>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400 font-medium">Show FBR Logo:</span>
                  <input
                    type="checkbox"
                    checked={showFbrPreview}
                    onChange={(e) => setShowFbrPreview(e.target.checked)}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Simulated Paper Header View (Matching User's Photo) */}
              <div className="p-4 bg-slate-100 rounded-2xl border border-slate-200 flex justify-center">
                <div className="w-full bg-white p-6 shadow-sm border border-slate-300 font-sans text-black select-none">
                  {/* Highlighted Section (Matches the red circle in user photo) */}
                  <div className="p-3 border-2 border-dashed border-red-500 rounded-xl bg-red-50/20 text-center transition-all">
                    <h2 className="text-lg font-bold tracking-tight text-black m-0 uppercase leading-snug">
                      {form.companyName || 'COMPANY NAME'}
                    </h2>
                    <div className="text-[10px] leading-relaxed text-slate-800 mt-1 uppercase font-medium">
                      {form.address || 'COMPANY ADDRESS'}
                      {form.city ? `, ${form.city}` : ''}
                      {form.province ? `, ${form.province}` : ''}
                      <br />
                      <strong>NTN : </strong>{form.ntn || '4113099'}
                      <br />
                      <strong>STN : </strong>{form.stn || '0300411300919'}
                      {form.phone && (
                        <>
                          <br />
                          <strong>Phone : </strong>{form.phone}
                        </>
                      )}
                    </div>
                  </div>

                  {/* FBR Badge Preview */}
                  {showFbrPreview && (
                    <div className="text-center mt-4 pt-3 border-t border-slate-100">
                      <div className="flex items-center justify-center gap-4 mb-2">
                        <img
                          src="/fbr-logo.png"
                          alt="FBR"
                          className="h-10 object-contain"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none'
                          }}
                        />
                        <QRCodeSVG value="SAMPLE-FBR-QR-DATA" size={42} level="M" />
                      </div>
                      <div className="text-[10px] font-bold text-slate-800">
                        FBR Invoice #: 4113099DIBBQLVF139535
                      </div>
                    </div>
                  )}

                  {/* Sample Buyer & Items Dummy Rows */}
                  <div className="mt-4 pt-3 border-t border-slate-200 text-[9px] text-slate-500 space-y-1.5 opacity-60">
                    <div className="flex justify-between font-bold text-slate-700">
                      <span>Buyer: SAMPLE BUYER MILLS LTD</span>
                      <span>Date: 27/08/2026</span>
                    </div>
                    <div className="h-6 bg-slate-100 rounded border border-slate-200 flex items-center justify-between px-2 text-[8px] font-semibold text-slate-600">
                      <span>1. Machinery Parts (8448.5900)</span>
                      <span>100.00 KG × PKR 1,350.00</span>
                      <span className="font-bold">PKR 135,000.00</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Information Note */}
              <div className="p-3 bg-blue-50/70 border border-blue-100 rounded-2xl flex items-start gap-2.5 text-blue-900 text-xs">
                <Info className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <p className="text-[11px] leading-relaxed">
                  <strong>Instant Propagation:</strong> Saving these details automatically updates the top letterhead across all <strong>POS Sales Invoices</strong>, <strong>FBR e-Invoices</strong>, <strong>Quotations</strong>, and <strong>Purchase Invoices</strong>.
                </p>
              </div>

              {/* Bottom Quick Save */}
              <button
                type="button"
                onClick={() => handleSave()}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-2xl text-xs font-bold shadow-md transition-all active:scale-95 disabled:opacity-50"
              >
                <Sparkles className="w-4 h-4 text-amber-400" />
                {saving ? 'Saving...' : 'Apply & Save Settings'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
